import { DataTableDesignTokens } from '@primeuix/themes/aura/datatable';

export const dataTableTokens: DataTableDesignTokens = {
  root: {
    transitionDuration: '{transition.duration}',
  },
  header: {
    background: 'transparent',
    borderColor: '{datatable.border.color}',
    borderWidth: '0',
    color: '{content.color}',
    padding: '0.75rem 0',
    sm: {
      padding: '0.375rem 0',
    },
    lg: {
      padding: '1rem 0',
    },
  },
  headerCell: {
    background: '{content.background}',
    hoverBackground: '{content.hover.background}',
    hoverColor: '{content.hover.color}',
    selectedBackground: '{content.background}',
    selectedColor: '{highlight.color}',
    borderColor: '{datatable.border.color}',
    color: '{content.color}',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    focusRing: {
      width: '{focus.ring.width}',
      offset: '-1px',
      style: '{focus.ring.style}',
      color: '{focus.ring.color}',
      shadow: '{focus.ring.shadow}',
    },
    sm: {
      padding: '0.375rem 0.5rem',
    },
    lg: {
      padding: '1rem 1.25rem',
    },
  },
  columnTitle: {
    fontWeight: '600',
  },
  columnFooter: {
    fontWeight: '600',
  },
  columnResizer: {
    width: '0.5rem',
  },
  row: {
    background: '{content.background}',
    hoverBackground: '{content.hover.background}',
    hoverColor: '{content.hover.color}',
    selectedBackground: '{highlight.background}',
    selectedColor: '{highlight.color}',
    color: '{content.color}',
    focusRing: {
      width: '{focus.ring.width}',
      offset: '-1px',
      color: '{focus.ring.color}',
      shadow: '{focus.ring.shadow}',
      style: '{focus.ring.style}',
    },
  },
  rowToggleButton: {
    hoverBackground: '{content.hover.background}',
    hoverColor: '{text.color}',
    selectedHoverBackground: '{content.background}',
    selectedHoverColor: '{primary.color}',
    color: '{text.muted.color}',
    size: '1.75rem',
    borderRadius: '{datatable.row.toggle.button.size} / 2',
    focusRing: {
      width: '{focus.ring.width}',
      style: '{focus.ring.style}',
      offset: '{focus.ring.offset}',
      color: '{focus.ring.color}',
      shadow: '{focus.ring.shadow}',
    },
  },
  bodyCell: {
    borderColor: '{datatable.border.color}',
    padding: '0.75rem 1rem',
    sm: {
      padding: '0.375rem 0.5rem',
    },
    lg: {
      padding: '1rem 1.25rem',
    },
  },
  footerCell: {
    background: '{content.background}',
    borderColor: '{datatable.border.color}',
    color: '{content.color}',
    padding: '0.75rem 1rem',
    sm: {
      padding: '0.375rem 0.5rem',
    },
    lg: {
      padding: '1rem 1.25rem',
    },
  },
  footer: {
    background: '{content.background}',
    borderColor: '{datatable.border.color}',
    borderWidth: '1px',
    color: '{content.color}',
    padding: '0.75rem 1rem',
    sm: {
      padding: '0.375rem 0.5rem',
    },
    lg: {
      padding: '1rem 1.25rem',
    },
  },
  dropPoint: {
    color: '{primary.color}',
  },
  resizeIndicator: {
    width: '1px',
    color: '{primary.color}',
  },
  sortIcon: {
    color: '{text.muted.color}',
    hoverColor: '{text.hover.muted.color}',
    size: '0.875rem',
  },
  loadingIcon: {
    size: '2rem',
  },
  filter: {
    inlineGap: '0.5rem',
    overlaySelect: {
      background: '{overlay.select.background}',
      borderColor: '{overlay.select.border.color}',
      borderRadius: '{overlay.select.border.radius}',
      color: '{overlay.select.color}',
      shadow: '{overlay.select.shadow}',
    },
    overlayPopover: {
      background: '{overlay.popover.background}',
      borderColor: '{overlay.popover.border.color}',
      borderRadius: '{overlay.popover.border.radius}',
      color: '{overlay.popover.color}',
      shadow: '{overlay.popover.shadow}',
      padding: '{overlay.popover.padding}',
      gap: '0.5rem',
    },
    rule: {
      borderColor: '{content.border.color}',
    },
    constraintList: {
      padding: '{list.padding}',
      gap: '{list.gap}',
    },
    constraint: {
      focusBackground: '{list.option.focus.background}',
      focusColor: '{list.option.focus.color}',
      selectedBackground: '{list.option.selected.background}',
      selectedFocusBackground: '{list.option.selected.focus.background}',
      selectedFocusColor: '{list.option.selected.focus.color}',
      selectedColor: '{list.option.selected.color}',
      color: '{list.option.color}',
      separator: {
        borderColor: '{content.border.color}',
      },
      padding: '{list.option.padding}',
      borderRadius: '{list.option.border.radius}',
    },
  },

  paginatorTop: {
    borderColor: '{datatable.border.color}',
    borderWidth: '1px',
  },
  paginatorBottom: {
    borderColor: '{datatable.border.color}',
    borderWidth: '1px',
  },
  css: ({ dt }) => `
    .p-datatable thead th {
      white-space: nowrap;
    }

    .p-datatable .p-datatable-table-container {
      border: 1px solid ${dt('datatable.header.border.color')};
      border-radius: ${dt('border.radius.md')};
      background-color: ${dt('content.background')};
    }

    .p-datatable .p-datatable-table-container .p-sortable-column-icon {
      width: ${dt('datatable.sort.icon.size')};
      height: ${dt('datatable.sort.icon.size')};
    }

    .p-datatable p-paginator {
      display: flex;
      justify-content: center;
    }

    .p-datatable .p-paginator {
      width: fit-content;
      margin: 0.75rem auto 0;
    }
  `,
  colorScheme: {
    light: {
      root: {
        borderColor: '{content.border.color}',
      },
      row: {
        stripedBackground: '{surface.50}',
      },
      bodyCell: {
        selectedBorderColor: '{primary.100}',
      },
    },
    dark: {
      root: {
        borderColor: '{surface.500}',
      },
      row: {
        stripedBackground: '{surface.950}',
      },
      bodyCell: {
        selectedBorderColor: '{primary.900}',
      },
    },
  },
};
